// react
import React, { ReactNode } from "react";

// utils
import avatarURL from "../../utils/avatarURL";

// styles
import css from "./Avatar.module.scss";

interface BaseProps {
	/**
	 * Render a square avatar
	 * @default false
	 */
	square?: boolean;
	/**
	 * Produces a title attribute on the image element
	 */
	title?: string;
	/**
	 * The id of the user to render the avatar for
	 */
	id?: string;
	/**
	 * The url of the avatar image, this will override the default behavior.
	 */
	url?: string;
	/**
	 * Add an alt attribute to the image element. If not provided, the alt
	 * attribute will be set to the user's id.
	 */
	label?: string | React.ReactNode;
	/**
	 * Additional class names to apply to the root element
	 * @default
	 */
	className?: string;
	/**
	 * The size of the avatar in pixels
	 * @default 100
	 */
	size?: number;
	/**
	 * Additional styles to apply to the root element. Using css classes is
	 * preferred.
	 * @default {}
	 * @example { border: "2px solid red" }
	 */
	style?: React.CSSProperties;
	onClick?: () => void;
	clickableLabel?: string | ReactNode;
	testId?: string;
}
const anonymousAvatarUrl = "https://dev-media.performyard.com/static/img/anonymous.png";

const Avatar = (props: BaseProps) => {
	const {
		id = null,
		url = null,
		label,
		size = 100,
		style = {},
		square = false,
		className = "",
		title,
		onClick,
		clickableLabel,
		testId = null,
	} = props;

	// Determine image source from provided props
	let src = "";
	if (!url && !id) {
		src = anonymousAvatarUrl;
	} else if (url) {
		src = url;
	} else if (id) {
		src = avatarURL(id);
	}

	const imageStyle = square ? { borderRadius: 0 } : {};

	// Determine image alt text from provided props
	let alt = "";
	if (label) {
		alt = label.toString();
	} else if (id) {
		alt = id;
	}

	const imageStyleProps = {
		...(size ? { height: size, width: size } : {}),
		...style,
	};

	return (
		<div
			title={title}
			className={`${css.avatar} cp-avatar ${className}`}
			style={imageStyleProps}
			data-testid={testId}
		>
			<img style={imageStyle} alt={alt} src={src} />
			{clickableLabel && (
				<span tabIndex={0} className={css.clickableLabel} onClick={onClick}>
					{clickableLabel}
				</span>
			)}
		</div>
	);
};

export default Avatar;
